/**
 * RouteViewTabs Component
 * Displays tabs for switching between Current Route and Day Route views
 */

import React from 'react';
import { ROUTE_VIEW_MODE, BOOKING_STATUS, type RouteViewMode } from '../../../constants/booking';
import { getTabButtonStyles, getBorderColor } from '../utils/styles';
import type { Booking, Ambulance } from '../../../services/api';

interface RouteViewTabsProps {
  selectedAmbulance: Ambulance | null;
  routeViewMode: RouteViewMode;
  activeBookings: Booking[];
  isDark: boolean;
  onCurrentRouteClick: () => Promise<void>;
  onDayRouteClick: () => void;
  /** Renders on the same row as tabs, aligned to the right (e.g. day-route date picker) */
  endSlot?: React.ReactNode;
}

/**
 * RouteViewTabs - Tab navigation for route views
 */
export const RouteViewTabs: React.FC<RouteViewTabsProps> = ({
  selectedAmbulance,
  routeViewMode,
  activeBookings,
  isDark,
  onCurrentRouteClick,
  onDayRouteClick,
  endSlot,
}) => {
  if (!selectedAmbulance) {
    return null;
  }

  // Only show Current Route tab when trip has started (in_transit, patient_picked, or patient_dropped)
  const hasStartedTrip = activeBookings.some(
    (b) =>
      b.spec.ambulanceId === selectedAmbulance._id &&
      (b.spec.status === BOOKING_STATUS.IN_TRANSIT ||
        b.spec.status === BOOKING_STATUS.PATIENT_PICKED ||
        b.spec.status === BOOKING_STATUS.PATIENT_DROPPED ||
        (b.spec.status as string) === 'arrived') // legacy
  );

  return (
    <div
      style={{
        marginBottom: '16px',
        borderBottom: `1px solid ${getBorderColor(isDark)}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {hasStartedTrip && (
            <button
              type="button"
              onClick={onCurrentRouteClick}
              style={getTabButtonStyles(
                routeViewMode === ROUTE_VIEW_MODE.CURRENT,
                isDark
              )}
            >
              Current Route
            </button>
          )}
          <button
            type="button"
            onClick={onDayRouteClick}
            style={getTabButtonStyles(
              routeViewMode === ROUTE_VIEW_MODE.DAY,
              isDark
            )}
          >
            Day Route
          </button>
        </div>
        {endSlot != null && endSlot !== false && (
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0,
            }}
          >
            {endSlot}
          </div>
        )}
      </div>
    </div>
  );
};

